<template>
  <Modal name="flag-issue" :custom="true" class="flag-issue-modal" @before-open="handleOpen">
    <div class="flag-issue-modal__inner">
      <div v-if="issueSubmitted" class="flag-issue-modal__success-wrapper">
        <CheckCircleIcon class="flag-issue-modal__success-icon" />

        <BravadoText tag="h5" variant="header5" class="flag-issue-modal__title">Thank You!</BravadoText>
        <BravadoText tag="p" variant="body" class="flag-issue-modal__description">
          We very much appreciate your help in making the War Room better. Our moderators have received the report and
          will address it shortly.
        </BravadoText>

        <footer>
          <BravadoButtonV3 variant="secondary" type="button" size="md" @click="closeModal">Got it</BravadoButtonV3>
        </footer>
      </div>

      <div v-else class="flag-issue-modal__form-wrapper">
        <BravadoText tag="h5" variant="header5" class="flag-issue-modal__title">Flag an issue</BravadoText>
        <BravadoText tag="p" variant="body" class="flag-issue-modal__description">
          Help us understand the problem and we will do our best to resolve it.
        </BravadoText>

        <form class="flag-issue-modal__form" @submit.prevent="handleSubmit">
          <BravadoTextInputV3
            v-model.trim="problemDescription"
            multiline
            :is-valid="problemDescriptionIsValid"
            placeholder="Describe the problem"
            :hint="problemDescriptionHint"
            @focus="attemptedSubmission = false"
          />

          <BravadoText class="flag-issue-modal__form-helper" tag="p">
            Not sure what the community rules are?
            <a href="//info.bravado.co/faq" class="flag-issue-modal__faq-link">Read the FAQ</a>
            for policy and guidelines. Reporting is anonymous.
          </BravadoText>

          <section class="flag-issue-modal__form-footer">
            <div>
              <BravadoButtonV3
                variant="secondary"
                type="button"
                :disabled="loading"
                class="flag-issue-modal__field"
                size="md"
                @click="closeModal"
              >
                Cancel
              </BravadoButtonV3>
            </div>

            <div>
              <BravadoButtonV3
                variant="accent"
                type="submit"
                size="md"
                :disabled="loading || $v.problemDescription.$invalid"
                class="flag-issue-modal__field"
              >
                Report
              </BravadoButtonV3>
            </div>
          </section>
        </form>
      </div>
    </div>
  </Modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, minLength } from 'vuelidate/lib/validators';
import CheckCircleIcon from '@/assets/svg/u_check-circle.vue.svg';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3';
import Modal from '~/components/common/Modal/Modal';
import FlagIssueMutation from '~/apollo/mutations/FlagIssue.mutation';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3';
import warRoomSignUpMixin from '~/components/partials/war-room/WarRoomSignUp/warRoomSignUpMixin';

export default {
  name: 'FlagIssueModal',
  components: {
    Modal,
    BravadoButtonV3,
    CheckCircleIcon,
    BravadoTextInputV3,
  },
  mixins: [validationMixin, warRoomSignUpMixin],
  data() {
    return {
      problemDescription: '',
      loading: false,
      attemptedSubmission: false,
      issueSubmitted: false,
    };
  },
  validations: {
    problemDescription: {
      required,
      minLength: minLength(10),
    },
  },
  computed: {
    problemDescriptionHint() {
      if (!this.$v.problemDescription.required) return 'Problem description is required';
      if (!this.$v.problemDescription.minLength) return 'Problem description must be at least 10 characters long';

      return '';
    },
    problemDescriptionIsValid() {
      return !(this.$v.problemDescription.$invalid && this.attemptedSubmission);
    },
  },
  methods: {
    async handleSubmit() {
      this.loading = true;
      this.attemptedSubmission = true;
      if (!this.$v.problemDescription.$invalid) {
        try {
          await this.$apollo.mutate({
            mutation: FlagIssueMutation,
            variables: {
              input: {
                flaggableId: this.$route.query.flaggableId,
                flaggableType: this.$route.query.flaggableType,
                text: this.problemDescription,
              },
            },
          });
          this.issueSubmitted = true;
        } catch (e) {
          this.$notice.error('Failed to send report, try again in few minutes.');
          this.$sentryUtils.error(e);
        } finally {
          this.loading = false;
        }
      }
    },
    closeModal() {
      this.$modal.hide();
    },
    handleOpen() {
      if (!this.$auth.loggedIn) {
        this.openWarRoomSignUpModal();
      }
    },
  },
};
</script>

<style lang="scss">
.flag-issue-modal {
  &__inner {
    top: 0;
    bottom: 0;
    width: 100%;
    overflow: hidden;
    position: absolute;
    padding: 24px 32px;
    color: var(--theme-color-text);
    background: var(--theme-color-card);

    @include mq('sm') {
      position: relative;
      max-width: 585px;
      overflow: auto;
      box-shadow: 0 16px 16px var(--black-a08), 0 8px 16px var(--black-a04);
      border-radius: 16px;
    }
  }

  &__title {
    margin-bottom: 12px;
  }

  &__description {
    margin-bottom: 24px;
  }

  &__textarea {
    height: 600px;
  }

  &__faq-link {
    font-weight: 600;
    text-decoration: underline;
  }

  &__field {
    margin-top: 24px;
    text-transform: capitalize;
  }

  &__success-wrapper {
    text-align: center;
  }

  &__success-icon {
    color: var(--blue-60);
  }

  &__form {
    margin-top: 21px;
  }

  &__form-helper {
    color: var(--theme-body);
    background-color: var(--theme-color-highlight);
    padding: 16px 20px;
    border-radius: 8px;
    margin-top: 16px;
  }

  &__form-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
